/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module UpdateModule */

import {
    AfterViewInit,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import {
    fipsOperationalHash,
    imageCategoryHash,
    imageTypeHash,
    IPrecheckProgressConfig,
    IRollbackOption,
    ISystemRollbackInfo,
    IUpdateConfig,
    IUserSelectedOptions,
    nodeTypesHash,
    UpdateWizardStage,
    UpgradePreChecksStatusTypeHash,
    UpgradeType,
} from 'ng/modules/update/update.types';

import {
    HttpWrapper,
    IHttpWrapperRequestConfig,
    THttpWrapper,
} from 'ajs/modules/core/factories/http-wrapper';

import {
    IUpgradeStatusInfo,
    RollbackType,
    SeGroupErrorRecovery,
} from 'generated-types';

import { AsyncFactory } from 'ajs/modules/core/factories/async-factory/async.factory';
import { Image } from 'ajs/modules/upgrade/factories/image.item.factory';
import { ImageCollection, TImageCollection } from 'ajs/modules/upgrade/factories/image-collection.factory';
import { InitialDataService } from 'ng/modules/core/services/initial-data/initial-data.service';
import { L10nService } from '@vmw/ngx-vip';
import { StringService } from 'string-service';
import { Subject } from 'rxjs';
import { SystemConfig } from 'ajs/modules/system/factories/system-config.item.factory';
import { SystemReportItem } from 'items/system-reports.item.factory';
import { SystemReportsCollection } from 'ng/modules/collections/factories/system-reports.collection.factory';
import { takeUntil } from 'rxjs/operators';
import { UpdateService } from 'ng/modules/update/services/update.service';
import * as globalL10n from 'global-l10n';

import {
    IAviCollectionDataGridConfig,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import './system-update.component.less';
import { UpdateStore } from '../update.store';
import * as l10n from './system-update.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

type TAsyncFactory = typeof AsyncFactory;

/**
 * Set of UpgradePreCheck Status.
 */
const PRE_CHECK_STATUS = new Set([
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_ERROR,
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_IN_PROGRESS,
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_STARTED,
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_WARNING,
]);

/**
 * Hash of different operation types received from backend for
 * operation type in System Report.
 */
const OPERATION_TYPES_HASH = {
    PRE_CHECKS: 'PRE-CHECKS',
    RESTORE: 'RESTORE',
    DRY_RUN: 'DRY RUN',
};

/**
 * @description
 * Component for System Update Page.
 * @author Vinay Jadhav, Deepak Chaudhary, Dhanashree Tilak
 */
@Component({
    selector: 'system-update',
    templateUrl: './system-update.component.html',
})
export class SystemUpdateComponent implements OnInit, AfterViewInit, OnDestroy {
    /**
     * Input to the component to provide SystemConfig service.
     */
    @Input('loadedSystemConfigService')
    public systemConfig: SystemConfig;

    /**
     * TemplateRef for Image#type column.
     */
    @ViewChild('imageTypeTemplateRef')
    public readonly imageTypeTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Set of L10n keys for component.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Set of global L10n keys.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Identifier for controller cluster.
     */
    public readonly upgradeNodeType = nodeTypesHash.NODE_CONTROLLER_CLUSTER;

    /**
     * Flag to enable rollback.
     */
    public enableRollback = false;

    /**
     * Flag to indicate if the component is in restore mode or not.
     */
    public isRestore = false;

    /**
     * Flag to indicate if the latest operation is Dry run.
     */
    public isDryRunFlow = false;

    /**
     * List of rollback options.
     */
    public rollbackOptions: IRollbackOption[] = [];

    /**
     * Collection for images
     */
    public imageCollection: ImageCollection;

    /**
     * Initial value for FIPS operational status. Defaults to FIPS_TRUE.
     */
    public fipsOperational = fipsOperationalHash.FIPS_TRUE;

    /**
     * AsyncFactory polling instance. Used to poll for the fips_operational flag.
     */
    public pollingFipsOperational: AsyncFactory = null;

    /**
     * Boolean indicating whether the system update page is for controller or not.
     */
    public readonly isControllerUpgrade = true;

    /**
     * Config object for grid listing images.
     */
    public imageCollectionGridConfig: IAviCollectionDataGridConfig;

    /**
     * List of nodes received from backend on trigger of pre-checks.
     */
    public preCheckNodes = this.updateStore.preCheckNodes$;

    /**
     * Whether the pre-checks are triggered or not.
     */
    public readonly arePreChecksTriggered$ = this.updateStore.preChecksTriggered$;

    /**
     * Upgrade Config details
     */
    public upgradeConfig: IUpdateConfig = {};

    /**
     * Wizard state
     */
    public isWizardOpen: boolean;

    /**
     * Overall state of pre-checks for all the selected nodes.
     */
    public readonly overallPreChecksState$ = this.updateStore.overallPreChecksState$;

    /**
     * List of Dry run nodes.
     */
    public readonly dryRunNodes$ = this.updateStore.dryRunNodes$;

    /**
     * Flag to track if the system update is in progress.
     */
    public isSystemUpdateInProgress = false;

    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private readonly httpWrapper: HttpWrapper;

    /**
     * The current stage of the Upgrade Wizard.
     */
    public currentUpdateWizardStage: UpdateWizardStage;

    /**
     * The progress wizard configuration.
     */
    public progressWizardInfo: IPrecheckProgressConfig;

    /**
     * Flag to track if flow is of type restore.
     */
    public isRestoreFlow = false;

    /**
     * Flag to track if flow is of type rollback.
     */
    public isRollback = false;

    /**
     * Flag to track if the initial configuration is set.
     */
    public isInitialConfigSet = false;

    public systemReportId: string;

    /**
     * Rollback-related information.
     */
    private rollbackInfo: ISystemRollbackInfo;

    /**
     * Flag to track if pre-checks are in progress.
     * This is needed in the component to deactivate the Upgrade button when
     * pre-checks are in progress.
     */
    public arePreChecksInProgress = false;

    /**
     * Subject used to unsubscribe observables.
     */
    private ngDestroyed$ = new Subject();

    /**
     * Options selected while triggering upgrade operation.
     */
    private userSelectedOptions: IUserSelectedOptions;

    /**
     * Contains information about the version of the system being upgraded.
     */
    private versionInformation: {
        /**
         * The full version string of the target version.
         */
        targetVersion: string,
        /**
         * The major version of the target version.
         * e.g. 22.1.1 => 22.1
         */
        targetMajorVersion: string,
        /**
         * The patch version of the target version.
         * e.g. 22.1.1-123456 => 123456
         */
        targetPatchVersion: string
    } = {
            targetVersion: '',
            targetMajorVersion: '',
            targetPatchVersion: '',
        };

    constructor(
        private readonly l10nService: L10nService,
        private readonly initDataService: InitialDataService,
        @Inject(ImageCollection)
        private readonly ImageCollectionFactory: TImageCollection,
        private readonly updateService: UpdateService,
        private readonly updateStore: UpdateStore,
        @Inject('AsyncFactory')
        private readonly AsyncFactory: TAsyncFactory,
        @Inject(HttpWrapper)
        private HttpWrapper: THttpWrapper,
        @Inject(SystemReportsCollection)
        private readonly SystemReportCollectionClass: typeof SystemReportsCollection,
        private readonly stringService: StringService,
    ) {
        this.setInitialUpgradeStatusInfo();
        l10nService.registerSourceBundles(dictionary);

        this.httpWrapper = new HttpWrapper();
        this.loadImages(this.initDataService.controllerVersion);
        this.setFipsOperationalPolling();
    }

    /** @override */
    public ngOnInit(): void {
        this.updateStore
            .isSystemUpdateInProgress$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(isSystemUpdateInProgress => {
                this.isSystemUpdateInProgress = isSystemUpdateInProgress;
            });

        this.updateStore
            .preChecksTriggered$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(arePreChecksInProgress => {
                this.arePreChecksInProgress = arePreChecksInProgress;
            });

        this.updateStore
            .isRestoreFlow$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(isRestoreFlow => this.isRestoreFlow = isRestoreFlow);

        this.updateStore
            .isDryRunFlow$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(isDryRunFlow => this.isDryRunFlow = isDryRunFlow);

        this.updateStore
            .userSelectedOptions$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(userSelectedOptions => this.userSelectedOptions = userSelectedOptions);
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.setGridConfig();
    }

    /**
     * Set config for system rollback, including type and the base version to be roll-backed to or
     * the full patch version that need to be reverted.
     * type - Rollback type, image or patch.
     */
    public getSystemRollbackConfig(rollbackType: RollbackType): IUpdateConfig {
        let targetVersion = '';
        const { rollbackInfo } = this;

        switch (rollbackType) {
            case RollbackType.ROLLBACK_TYPE_IMAGE:
                targetVersion = rollbackInfo?.previousPatchVersion ?
                    [rollbackInfo.previousVersion, rollbackInfo.previousPatchVersion].join('-') :
                    rollbackInfo.previousVersion;
                break;

            case RollbackType.ROLLBACK_TYPE_PATCH:
                targetVersion = `${rollbackInfo.version}-${rollbackInfo.patchVersion}`;
                break;
        }

        return {
            upgradeType: UpgradeType.UPGRADE_TYPE_SYSTEM_ROLLBACK,
            rollbackType,
            targetVersion,
        };
    }

    /**
     * Set options for rollback dropdown menu.
     * rollbackInfo - Info about rollback to generate options.
     */
    public setSystemRollbackOptions(rollbackInfo: ISystemRollbackInfo): void {
        this.rollbackOptions = [];

        if (rollbackInfo?.enableImageRollback) {
            const fullVersion = rollbackInfo?.patchVersion ?
                `${rollbackInfo?.version}-${rollbackInfo?.patchVersion}` : rollbackInfo?.version;

            const fullPreviousVersion = rollbackInfo?.previousPatchVersion ?
                `${rollbackInfo?.previousVersion}-${rollbackInfo?.previousPatchVersion}` :
                rollbackInfo?.previousVersion;

            const imageRollbackOption = {
                title: this.l10nService.getMessage(this.globalL10nKeys.versionLabel),
                value: RollbackType.ROLLBACK_TYPE_IMAGE,
                fullVersion,
                fullPreviousVersion,
            };

            this.rollbackOptions.push(imageRollbackOption);
        }

        if (rollbackInfo?.enablePatchRollback) {
            const patchRollbackOption = {
                title: this.l10nService.getMessage(this.l10nKeys.clearPatchLabel),
                value: RollbackType.ROLLBACK_TYPE_PATCH,
                fullVersion: `${rollbackInfo?.version}-${rollbackInfo?.patchVersion}`,
                fullPreviousVersion: `${rollbackInfo?.version}`,
            };

            this.rollbackOptions.push(patchRollbackOption);
        }
    }

    /**
     * Open wizard once the Rollback button is clicked from System about card.
     */
    public onRollbackOptionClick(rollbackType: RollbackType): void {
        this.upgradeConfig = this.getSystemRollbackConfig(rollbackType);
        this.currentUpdateWizardStage = UpdateWizardStage.LETS_BEGIN;
        this.isRollback = true;
        this.isWizardOpen = true;
    }

    /**
     * Set up an AsyncFactory instance to poll for the fips_operational flag. If the flag returns
     * FIPS_UNKNOWN, then we continue polling because it means that a warm reboot is still in
     * progress. If the status is either FIPS_TRUE or FIPS_FALSE then we can stop polling.
     */
    public setFipsOperationalPolling(): void {
        const requestConfig = {
            url: '/api/cluster/runtime',
            method: 'GET',
        };

        this.pollingFipsOperational = new this.AsyncFactory(async() => {
            const response = await this.httpWrapper.request(
                requestConfig as IHttpWrapperRequestConfig,
            );

            if (response && response.data) {
                const { node_info: nodeInfo } = response.data;

                this.fipsOperational = nodeInfo.fips_operational;

                if (this.fipsOperational !== fipsOperationalHash.FIPS_UNKNOWN) {
                    this.pollingFipsOperational.stop();
                }
            }
        });

        this.pollingFipsOperational.start(999);
    }

    /**
     * Open Pre-check wizard to view Pre-check progress.
     */
    public openProgressWizard(config: IPrecheckProgressConfig): void {
        this.progressWizardInfo = config;
        this.currentUpdateWizardStage = UpdateWizardStage.PRE_CHECK;
        this.isWizardOpen = true;
        this.isRestore = config.isRestoreFlow;

        if (this.userSelectedOptions.upgradeType) {
            this.upgradeConfig = {
                upgradeType: this.userSelectedOptions.upgradeType,
            };
        } else if (this.isRestore) {
            this.upgradeConfig = {
                upgradeType: UpgradeType.UPGRADE_TYPE_SYSTEM_RESTORE,
            };
        } else if (this.isDryRunFlow) {
            this.upgradeConfig = {
                upgradeType: UpgradeType.UPGRADE_TYPE_SYSTEM_DRYRUN,
            };
        } else if (this.isRollback) {
            this.upgradeConfig = {
                upgradeType: UpgradeType.UPGRADE_TYPE_SYSTEM_ROLLBACK,
            };
        } else {
            this.upgradeConfig = {
                upgradeType: UpgradeType.UPGRADE_TYPE_SYSTEM_UPGRADE,
            };
        }
    }

    /**
     * Open Wizard for restore when the restore button in the list view is clicked.
     */
    public openRestoreWizard(): void {
        this.upgradeConfig = {
            upgradeType: UpgradeType.UPGRADE_TYPE_SYSTEM_RESTORE,
        };

        this.currentUpdateWizardStage = UpdateWizardStage.LETS_BEGIN;
        this.isRestore = true;
        this.isWizardOpen = true;
    }

    /**
     * @override
     * Reset PreChecksNodes, preChecksTriggered and preCheckCompleted state
     * so the store doesn't hold the previously fetched value in case user navigates to another page
     * and then comes back to system update page.
     */
    public ngOnDestroy(): void {
        const {
            pollingFipsOperational,
            updateStore,
        } = this;

        pollingFipsOperational?.stop();
        updateStore.setPreCheckNodesState([]);
        updateStore.resetPreChecksTriggeredState(false);
        updateStore.resetPreChecksCompletedState(false);
        updateStore.resetOverallPreChecksState();
        updateStore.resetIsPreCheckOnlyFlowState();
        updateStore.setIsPreCheckFlowState(false);
        updateStore.setIsRestoreFlowState(false);
        updateStore.setIsDryRunFlowState(false);
        updateStore.setDryRunNodesState([]);
        updateStore.setIsUpdateTriggeredWithWarningState(false);

        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Set avi-collection-data-grid config
     */
    private setGridConfig(): void {
        this.imageCollectionGridConfig = {
            layout: {
                disableCreate: true,
                hideCreate: true,
                hideDelete: true,
                hideEdit: true,
            },
            id: `${this.imageCollection?.objectName}-list-page`,
            collection: this.imageCollection,
            fields: [
                {
                    label: this.l10nService.getMessage(this.globalL10nKeys.typeLabel),
                    id: 'type',
                    templateRef: this.imageTypeTemplateRef,
                },
                {
                    label: this.l10nService.getMessage(this.globalL10nKeys.versionLabel),
                    id: 'version',
                    transform: (row: Image): string => {
                        return row.isUsedByController() ?
                            this.l10nService.getMessage(
                                l10nKeys.currentVersionLabel,
                                [row.version],
                            ) : row.version;
                    },
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleAttributes),
                    id: 'attribute',
                    transform: (row: Image): string => row.category,
                },
            ],
            multipleactions: [
                {
                    id: 'update',
                    label: this.l10nService.getMessage(l10nKeys.updateLabel),
                    onClick: (rows: Image[]) => this.onUpgradeClick(rows),
                    disabled: (rows: Image[]) => {
                        return !this.isImageSelectionValid(rows) ||
                            this.isSystemUpdateInProgress ||
                            this.arePreChecksInProgress;
                    },
                },
            ],
            rowSelectionDisabled: (row: Image): boolean => this.isCheckboxDeactivated(row),
        };
    }

    /**
     * Deactivate selecting if:
     * 1. row is an active image or
     * 2. row is a image with a lower base version number than the active one or
     * 3. row is a se patch image or
     * 4. row is a system image with the current active base version number but with a lower
     * build number.
     * 5. row is an image with same base version but lower patch version.
     */
    private isCheckboxDeactivated(image: Image): boolean {
        if (image) {
            const {
                majorVersion,
                build,
                patchVersion,
            } = image;

            const {
                controllerVersion: controllerMajorVersion,
                controllerBuild,
                controllerPatch,
            } = this.initDataService;

            const isOlderBuild = image.isType(imageTypeHash.IMAGE_TYPE_SYSTEM) &&
                (majorVersion === controllerMajorVersion && build <= controllerBuild);

            const isLowerPatchVersion = image.isType(imageTypeHash.IMAGE_TYPE_PATCH) &&
                (majorVersion === controllerMajorVersion && patchVersion <= controllerPatch);

            return image.isUsedByController() ||
                UpdateService.compareMajorVersions(majorVersion, controllerMajorVersion) < 0 ||
                image.isCategory(imageCategoryHash.IMAGE_CATEGORY_SE) ||
                isOlderBuild || isLowerPatchVersion;
        }

        return false;
    }

    /**
     * Check if image selection is valid for upgrade.
     */
    private isImageSelectionValid(selectedImages: any): boolean {
        return this.updateService.isImageListValidForUpgrade(
            selectedImages,
            UpgradeType.UPGRADE_TYPE_SYSTEM_UPGRADE,
        );
    }

    /**
     * Open Wizard for upgrade when the upgrade button in the list view is clicked.
     */
    private onUpgradeClick(image: Image[]): void {
        this.upgradeConfig = this.getSystemUpgradeConfig(image);
        this.isWizardOpen = true;
        this.currentUpdateWizardStage = UpdateWizardStage.LETS_BEGIN;
    }

    /**
     * Set version information
     */
    private setUpgradeTargetVersions(
        version: string,
        majorVersion: string,
        patchVersion = '',
    ): void {
        this.versionInformation.targetVersion = version;
        this.versionInformation.targetMajorVersion = majorVersion;
        this.versionInformation.targetPatchVersion = patchVersion;
    }

    /**
     * Set config for system upgrade by retrieving image ids and versions.
     */
    private getSystemUpgradeConfig(images: Image[]): IUpdateConfig {
        let systemImageId = '';
        let controllerPatchImageId = '';
        let sePatchImageId = '';

        const {
            IMAGE_TYPE_PATCH,
            IMAGE_TYPE_SYSTEM,
        } = imageTypeHash;

        if (images && Array.isArray(images) && images.length) {
            images.forEach(
                (image: Image) => {
                    const {
                        type,
                        category,
                        id,
                        version,
                        majorVersion,
                        patchVersion,
                    } = image;

                    const { IMAGE_CATEGORY_HYBRID } = imageCategoryHash;

                    switch (type) {
                        case IMAGE_TYPE_SYSTEM:
                            systemImageId = id;
                            this.setUpgradeTargetVersions(version, majorVersion);
                            break;

                        case IMAGE_TYPE_PATCH:

                            if (category === IMAGE_CATEGORY_HYBRID) {
                                sePatchImageId = id;
                            }

                            controllerPatchImageId = id;

                            if (!this.versionInformation.targetVersion) {
                                this.setUpgradeTargetVersions(
                                    version,
                                    majorVersion,
                                    patchVersion,
                                );
                            }

                            break;
                    }
                },
            );
        }

        return {
            upgradeType: UpgradeType.UPGRADE_TYPE_SYSTEM_UPGRADE,
            systemImageId,
            controllerPatchImageId,
            sePatchImageId,
            ...this.versionInformation,
        };
    }

    /**
     * Create image collection from raw inventory object
     */
    private loadImages(controllerMajorVersion: string): void {
        this.imageCollection = new this.ImageCollectionFactory({
            objectName: 'image-raw-inventory',
            params: {
                base_image_version: controllerMajorVersion,
                exclude_se_patch: true,
            },
            searchFields: ['type', 'version'],
        });
        this.setGridConfig();
    }

    /**
     * Update store state based on the provided input value.
     */
    public handleSelectSegUpdate(isSegUpgrade: boolean): void {
        this.updateStore.setIsSegUpgrade(isSegUpgrade);
    }

    /**
     * Update state when the wizard is closed.
     */
    public onClose(): void {
        this.isWizardOpen = false;
        this.isRestore = false;
        this.isRollback = false;
        this.currentUpdateWizardStage = undefined;
    }

    /**
     * Return true if FIPS mode has been configured but is non operational.
     */
    public get fipsFailure(): boolean {
        return this.systemConfig?.config?.fips_mode &&
            this.fipsOperational === fipsOperationalHash.FIPS_FALSE;
    }

    /**
     * Fetch overall state and update the polling parameters and data for system rollback.
     */
    private async setInitialUpgradeStatusInfo(): Promise<void> {
        const {
            updateService,
            updateStore,
        } = this;

        const { UPGRADE_PRE_CHECK_IN_PROGRESS } = UpgradePreChecksStatusTypeHash;

        let operationType = '';
        let systemReportItem: SystemReportItem;

        const response: {
            data: {
                count: number,
                results: IUpgradeStatusInfo[],
            },
        } = await updateService.getUpgradeStatus();

        if (response?.data?.count && response.data.results?.length) {
            const controllerUpgradeStatus = response.data.results[0];
            const state = controllerUpgradeStatus.upgrade_readiness?.state?.state;
            const systemReports = controllerUpgradeStatus.system_report_refs;

            if (controllerUpgradeStatus.system_report_refs?.length) {
                systemReportItem = await this.getSystemReportItem(systemReports[0]);

                operationType = systemReportItem?.data?.config?.type;
            }

            this.setSystemRollbackInfo(controllerUpgradeStatus);

            if (operationType) {
                updateStore.setIsPreCheckOnlyFlowState(operationType === OPERATION_TYPES_HASH.PRE_CHECKS);
                updateStore.setIsRestoreFlowState(operationType === OPERATION_TYPES_HASH.RESTORE);

                if (operationType === OPERATION_TYPES_HASH.DRY_RUN) {
                    if (!systemReportItem.data.config.dryrun_info) {
                        if (PRE_CHECK_STATUS.has(state)) {
                            if (state === UPGRADE_PRE_CHECK_IN_PROGRESS) {
                                updateStore.onPreChecksTrigger();
                            }

                            updateStore.setIsPreCheckFlowState(true);
                        } else {
                            updateStore.setIsPreCheckFlowState(false);
                        }
                    }

                    updateStore.setIsDryRunFlowState(true);
                } else if (PRE_CHECK_STATUS.has(state)) {
                    if (state === UPGRADE_PRE_CHECK_IN_PROGRESS) {
                        updateStore.onPreChecksTrigger();
                    }

                    this.setSuppressWarning(controllerUpgradeStatus);
                    updateStore.setIsPreCheckFlowState(true);
                } else {
                    updateStore.setIsPreCheckFlowState(false);
                }
            }
        } else {
            updateStore.setIsPreCheckFlowState(false);
        }

        this.isInitialConfigSet = true;
    }

    /**
     * Set the suppress warning for Controller after refresh based on response.
     */
    private setSuppressWarning(controllerUpgradeStatus: IUpgradeStatusInfo): void {
        const {
            upgrade_readiness: upgradeReadiness,
            enable_rollback: enableRollback,
            enable_patch_rollback: enablePatchRollback,
        } = controllerUpgradeStatus;
        const imageRef = upgradeReadiness?.image_ref ?? upgradeReadiness?.patch_image_ref;
        const systemImageId = this.stringService.slug(imageRef);
        const selectedItem = this.imageCollection.getItemById(systemImageId);
        let userSelectedOptions: IUserSelectedOptions = {
            actionOnSegFailure: SeGroupErrorRecovery.SUSPEND_UPGRADE_OPS_ON_ERROR,
        };

        if (enableRollback || enablePatchRollback) {
            this.upgradeConfig = enableRollback ?
                this.getSystemRollbackConfig(RollbackType.ROLLBACK_TYPE_IMAGE) :
                this.getSystemRollbackConfig(RollbackType.ROLLBACK_TYPE_PATCH);

            userSelectedOptions = {
                ...userSelectedOptions,
                applyToSystem: UpdateService.isSegUpgradeAvailable(this.upgradeConfig),
                upgradeConfig: this.upgradeConfig,
                upgradeType: this.upgradeConfig.upgradeType,
                isRollback: true,
            };
        } else if (selectedItem) {
            this.upgradeConfig = this.getSystemUpgradeConfig([selectedItem]);
            userSelectedOptions = {
                ...userSelectedOptions,
                applyToSystem: UpdateService.isSegUpgradeAvailable(this.upgradeConfig),
                upgradeConfig: this.upgradeConfig,
                upgradeType: this.upgradeConfig.upgradeType,
                isRollback: false,
            };
        }

        this.updateStore.setUserSelectedOptionsState(userSelectedOptions);
        this.updateStore.setIsSystemUpdatePageState(true);
    }

    /**
     * Set SystemRollback related properties.
     */
    private setSystemRollbackInfo(controllerUpgradeStatus: IUpgradeStatusInfo): void {
        let { version } = controllerUpgradeStatus;

        this.enableRollback = controllerUpgradeStatus?.enable_rollback ||
            controllerUpgradeStatus?.enable_patch_rollback;

        if (this.enableRollback) {
            const currentVersionHash = UpdateService.getVersionAsHash(version);
            const previousVersionHash =
                UpdateService.getVersionAsHash(controllerUpgradeStatus.previous_version);

            controllerUpgradeStatus.previous_version = previousVersionHash?.version;

            if (controllerUpgradeStatus?.enable_rollback) {
                if (currentVersionHash?.version === previousVersionHash?.version) {
                    const { build: currentBuild } = currentVersionHash;
                    const { build: previousBuild } = previousVersionHash;

                    version = `${currentVersionHash?.version}-${currentBuild}`;
                    controllerUpgradeStatus.previous_version =
                        `${previousVersionHash?.version}-${previousBuild}`;
                }
            }

            this.rollbackInfo = {
                version: currentVersionHash?.version,
                patchVersion: controllerUpgradeStatus?.patch_version,
                previousVersion: controllerUpgradeStatus?.previous_version,
                previousPatchVersion: controllerUpgradeStatus?.previous_patch_version,
                enableImageRollback: controllerUpgradeStatus?.enable_rollback,
                enablePatchRollback: controllerUpgradeStatus?.enable_patch_rollback,
            };

            this.setSystemRollbackOptions(this.rollbackInfo);
        }
    }

    /**
     * Check if the latest operation is of type pre check only.
     */
    private async getSystemReportItem(systemReportRef: string): Promise<SystemReportItem> {
        const systemReportCollection = new this.SystemReportCollectionClass();

        this.systemReportId = this.stringService.slug(systemReportRef);

        systemReportCollection.setParams({ 'uuid.in': [this.systemReportId] });

        await systemReportCollection.load();

        const systemReportItem = { ...systemReportCollection.getItemById(this.systemReportId) };

        systemReportCollection.destroy();

        return systemReportItem;
    }
}
